<template>
  <div class="linehaul-calculator">
    <!-- Page Header -->
    <div class="page-header">
      <v-container fluid class="px-6">
        <div class="d-flex align-center py-5">
          <div class="header-icon-badge mr-4">
            <v-icon color="white" large>fas fa-route</v-icon>
          </div>
          <div>
            <h1 class="text-h4 font-weight-bold white--text">Line-haul Calculator</h1>
            <p class="body-1 mb-0" style="color: rgba(255,255,255,0.8)">Find optimal line-haul routes between production and consolidation depots</p>
          </div>
        </div>
      </v-container>
    </div>

    <v-container fluid class="px-6 pt-6 pb-10 page-body">
      <!-- Input Form Card -->
      <v-card class="mb-8 form-card" rounded="lg">
        <div class="form-card-accent"></div>
        <v-card-text class="pa-6">
          <div class="d-flex align-center mb-4">
            <v-icon color="primary" class="mr-2">fas fa-sliders-h</v-icon>
            <span class="subtitle-1 font-weight-bold grey--text text--darken-3">Line-haul Parameters</span>
          </div>
          <v-row align="center">
            <v-col cols="12" md="2">
              <v-text-field
                v-model="trackingNumber"
                label="Tracking Number"
                prepend-inner-icon="fas fa-barcode"
                outlined
                dense
                clearable
                hide-details="auto"
                placeholder="e.g. ABC123"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="2">
              <v-autocomplete
                v-model="selectedDepots"
                :items="wmsDepots"
                item-text="name"
                item-value="id"
                label="Production Depot(s)"
                prepend-inner-icon="fas fa-warehouse"
                multiple
                chips
                small-chips
                deletable-chips
                clearable
                outlined
                dense
                :loading="depotsLoading"
                :disabled="!!trackingNumber"
                hide-details="auto"
              ></v-autocomplete>
            </v-col>

            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="deliverySuburb"
                :items="suburbSuggestions"
                :search-input.sync="suburbSearch"
                :loading="suburbLoading"
                item-text="display"
                item-value="display"
                label="Delivery Suburb"
                prepend-inner-icon="fas fa-search-location"
                clearable
                outlined
                dense
                hide-no-data
                hide-details="auto"
                placeholder="Type suburb or postcode..."
                :disabled="!!trackingNumber"
                @change="onSuburbSelect"
              ></v-autocomplete>
            </v-col>

            <v-col cols="12" md="2">
              <DatePicker
                ref="deliveryDatePicker"
                label="Delivery Date"
                :date_props="deliveryDate"
                @interface="onDeliveryDateChange"
                :allowedDates="allowedDeliveryDates"
                outlined
                dense
                clearable
                :hideDetails="'auto'"
                :disabled="!!trackingNumber"
              />
            </v-col>

            <v-col cols="12" md="3" class="d-flex align-center">
              <v-btn
                color="primary"
                :disabled="!canShowOptions"
                :loading="optionsLoading"
                @click="showOptions"
                large
                depressed
                class="text-none font-weight-bold search-btn px-8"
              >
                <v-icon left>fas fa-search</v-icon>
                Show Options
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Results -->
      <template v-if="hasResults">
        <!-- Results Summary Banner -->
        <v-card class="mb-6 summary-banner" rounded="lg">
          <v-card-text class="pa-5">
            <v-row align="center" no-gutters>
              <v-col cols="12" md="8">
                <div class="d-flex align-center">
                  <div class="summary-badge mr-4">
                    <v-icon color="white" size="20">fas fa-check-circle</v-icon>
                  </div>
                  <div>
                    <div class="body-1 font-weight-bold grey--text text--darken-4">
                      {{ routeOptions.length }} Line-haul option{{ routeOptions.length !== 1 ? 's' : '' }} found
                    </div>
                    <div class="d-flex align-center mt-1 flex-wrap">
                      <v-chip color="green" dark small class="font-weight-bold mr-2">
                        <v-icon left x-small>fas fa-industry</v-icon>
                        {{ resultOriginDepotName }}
                      </v-chip>
                      <v-icon small color="grey" class="mx-1">fas fa-long-arrow-alt-right</v-icon>
                      <v-chip color="red darken-1" dark small class="font-weight-bold">
                        <v-icon left x-small>fas fa-warehouse</v-icon>
                        {{ resultDestinationDepotName }}
                      </v-chip>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="4" class="text-md-right mt-3 mt-md-0">
                <span class="caption grey--text text--darken-1">Click a row to view details</span>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Results Table -->
        <v-card class="mb-6 table-card" rounded="lg">
          <v-data-table
            :headers="tableHeaders"
            :items="routeOptions"
            :items-per-page="10"
            class="routes-table"
          >
            <template v-slot:item="{ item }">
              <tr
                @click="toggleDetails(item)"
                :style="{
                  cursor: 'pointer',
                  backgroundColor: selectedOption === item ? '#bbdefb' : '',
                  boxShadow: selectedOption === item ? 'inset 3px 0 0 #1565c0' : '',
                }"
              >
                <td>{{ getLatestProductionDate(item) || '-' }}</td>
                <td>{{ item.latest_consolidation_date || '-' }}</td>
                <td>{{ item.delivery_ready_date || '-' }}</td>
                <td>
                  <span class="font-weight-bold" :class="item.price != null ? 'green--text text--darken-2' : ''">
                    {{ item.price != null ? '$' + item.price : '-' }}
                  </span>
                </td>
                <td>
                  <v-chip small color="blue-grey lighten-4" class="font-weight-bold">
                    {{ item.depots ? item.depots.length - 1 : '-' }}
                  </v-chip>
                </td>
                <td>
                  <v-btn icon small class="expand-btn" @click.stop="toggleDetails(item)">
                    <v-icon size="20">{{ selectedOption === item ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>

        <!-- Detail Panel -->
        <v-expand-transition>
          <v-card v-if="selectedOption" class="mb-6 detail-card" rounded="lg">
            <div class="detail-card-header pa-5">
              <div class="d-flex align-center">
                <div class="detail-header-badge mr-3">
                  <v-icon color="white" size="18">fas fa-info-circle</v-icon>
                </div>
                <span class="text-h6 font-weight-bold white--text">Line-haul Details</span>
                <v-spacer></v-spacer>
                <v-btn icon dark @click="selectedOption = null">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>
            </div>

            <v-card-text class="pa-6">
              <!-- Stat Cards Row -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6" md="3">
                  <div class="stat-card stat-card--indigo">
                    <div class="stat-card__icon">
                      <v-icon color="white" size="20">fas fa-calendar-check</v-icon>
                    </div>
                    <div class="stat-card__content">
                      <div class="stat-card__label">Latest Production</div>
                      <div class="stat-card__value">{{ latestProductionDate || '-' }}</div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <div class="stat-card stat-card--blue">
                    <div class="stat-card__icon">
                      <v-icon color="white" size="20">fas fa-boxes</v-icon>
                    </div>
                    <div class="stat-card__content">
                      <div class="stat-card__label">Latest Consolidation</div>
                      <div class="stat-card__value">{{ selectedOption.latest_consolidation_date || '-' }}</div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <div class="stat-card stat-card--teal">
                    <div class="stat-card__icon">
                      <v-icon color="white" size="20">fas fa-truck-loading</v-icon>
                    </div>
                    <div class="stat-card__content">
                      <div class="stat-card__label">Delivery Ready</div>
                      <div class="stat-card__value">{{ selectedOption.delivery_ready_date || '-' }}</div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <div class="stat-card stat-card--green">
                    <div class="stat-card__icon">
                      <v-icon color="white" size="20">fas fa-dollar-sign</v-icon>
                    </div>
                    <div class="stat-card__content">
                      <div class="stat-card__label">Price</div>
                      <div class="stat-card__value">{{ selectedOption.price != null ? '$' + selectedOption.price : '-' }}</div>
                    </div>
                  </div>
                </v-col>
              </v-row>

              <v-divider class="my-6"></v-divider>

              <!-- Line-haul Map -->
              <div class="section-header mb-4">
                <v-icon color="primary" class="mr-2">fas fa-map-marked-alt</v-icon>
                <span class="text-h6 font-weight-bold grey--text text--darken-3">Line-haul Map</span>
              </div>
              <v-card outlined rounded="lg" class="mb-6 map-container overflow-hidden">
                <div style="height: 95vh; position: relative;">
                  <div v-if="mapLoading" class="map-loading-overlay">
                    <v-progress-circular indeterminate color="primary" size="56" width="5"></v-progress-circular>
                    <div class="mt-3 body-1 grey--text text--darken-1">Loading Line-haul map...</div>
                  </div>
                  <v-btn
                    v-if="initialBounds"
                    fab
                    small
                    color="white"
                    class="reset-map-btn"
                    @click="resetMap"
                    title="Reset map view"
                  >
                    <v-icon>mdi-crosshairs-gps</v-icon>
                  </v-btn>
                  <l-map
                    ref="routeMap"
                    :zoom="4"
                    :center="[-25.5, 134.5]"
                    :min-zoom="4"
                    :max-zoom="18"
                    :max-bounds="australiaBounds"
                    :max-bounds-viscosity="1.0"
                    :options="{ zoomControl: true, attributionControl: true }"
                    style="height: 100%; width: 100%; z-index: 1;"
                    @ready="onRouteMapReady"
                  >
                    <l-tile-layer :url="tileUrl"></l-tile-layer>
                    <l-polyline
                      v-for="(leg, lIndex) in routeMapLegs"
                      :key="'leg-' + lIndex"
                      :lat-lngs="leg.path"
                      :color="leg.color"
                      :weight="3"
                      :opacity="0.8"
                    ></l-polyline>
                    <l-marker
                      v-for="(leg, lIndex) in routeMapLegs"
                      :key="'legnum-' + lIndex"
                      :lat-lng="leg.midpoint"
                      :icon="createLegIcon(lIndex + 1, leg.color)"
                    ></l-marker>
                    <l-marker
                      v-for="(leg, lIndex) in routeMapLegs"
                      :key="'arrow-' + lIndex"
                      :lat-lng="leg.arrowPoint"
                      :icon="createArrowIcon(leg.bearing, leg.color)"
                    ></l-marker>
                    <l-marker
                      v-for="(coords, mIndex) in routeMapMarkers"
                      :key="'marker-' + mIndex"
                      :lat-lng="coords.latLng"
                      :icon="createDepotIcon(mIndex, routeMapMarkers.length)"
                    >
                      <l-tooltip :options="{ permanent: true, direction: tooltipDirection(mIndex), offset: tooltipOffset(mIndex) }">
                        {{ coords.name }} {{ coords.dateLabel }}
                      </l-tooltip>
                    </l-marker>
                  </l-map>
                </div>
              </v-card>

              <v-divider class="my-6"></v-divider>

              <!-- Line-haul Hops -->
              <div class="section-header mb-4">
                <v-icon color="primary" class="mr-2">fas fa-project-diagram</v-icon>
                <span class="text-h6 font-weight-bold grey--text text--darken-3">Line-haul Hops</span>
                <v-chip small color="primary" dark class="ml-3 font-weight-bold">
                  {{ selectedOptionDepots.length }} stop{{ selectedOptionDepots.length !== 1 ? 's' : '' }}
                </v-chip>
                <v-chip small outlined color="blue-grey" class="ml-2 font-weight-bold">
                  {{ selectedOption.depots ? selectedOption.depots.length - 1 : '-' }} leg{{ (selectedOption.depots ? selectedOption.depots.length - 1 : 0) !== 1 ? 's' : '' }}
                </v-chip>
              </div>
              <v-timeline dense align-top v-if="selectedOptionDepots.length">
                <v-timeline-item
                  v-for="(hop, hIndex) in selectedOptionDepots"
                  :key="hIndex"
                  :color="hopColor(hIndex)"
                  fill-dot
                  large
                >
                  <template v-slot:icon>
                    <span class="white--text font-weight-bold" style="font-size: 13px;">{{ hIndex + 1 }}</span>
                  </template>
                  <v-card class="hop-card" outlined rounded="lg">
                    <v-card-text class="pa-4">
                      <div class="d-flex align-center justify-space-between flex-wrap">
                        <div class="d-flex align-center">
                          <div>
                            <div class="d-flex align-center mb-1">
                              <span class="subtitle-1 font-weight-bold grey--text text--darken-4">
                                {{ getDepotName(hop.depot_id) || ('Depot ' + hop.depot_id) }}
                              </span>
                              <v-chip
                                x-small
                                :color="hopColor(hIndex)"
                                dark
                                class="ml-2 font-weight-bold"
                              >
                                {{ hopLabel(hIndex) }}
                              </v-chip>
                            </div>
                          </div>
                        </div>
                        <div class="d-flex align-center mt-2 mt-sm-0">
                          <div v-if="hop.departure_date" class="hop-date-badge mr-3">
                            <v-icon x-small color="blue darken-1" class="mr-1">fas fa-truck</v-icon>
                            <span class="caption font-weight-bold blue--text text--darken-1">{{ hop.departure_date }}</span>
                          </div>
                          <div v-if="hop.arrival_date" class="hop-date-badge">
                            <v-icon x-small color="green darken-1" class="mr-1">fas fa-truck-loading</v-icon>
                            <span class="caption font-weight-bold green--text text--darken-1">{{ hop.arrival_date }}</span>
                          </div>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-timeline-item>
              </v-timeline>
            </v-card-text>
          </v-card>
        </v-expand-transition>
      </template>

      <!-- No results message -->
      <v-card v-if="noResults" class="mb-4" rounded="lg">
        <v-card-text class="pa-6">
          <v-alert type="warning" prominent outlined class="mb-0">
            <div class="body-1">No line-haul routes found for the selected criteria. Please try different parameters.</div>
          </v-alert>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import axios from 'axios';
import L from 'leaflet';
import { LMap, LTileLayer, LMarker, LTooltip, LPolyline } from 'vue2-leaflet';
import 'leaflet/dist/leaflet.css';
import DatePicker from '../../parts/DatePicker.vue';

export default {
  name: "LinehaulCalculator",
  components: {
    DatePicker,
    LMap,
    LTileLayer,
    LMarker,
    LTooltip,
    LPolyline,
  },
  data() {
    return {
      // Form inputs
      trackingNumber: null,
      selectedDepots: [],
      deliverySuburb: null,
      selectedSuburbData: null,
      suburbSearch: null,
      deliveryDate: null,

      // Loading states
      depotsLoading: false,
      suburbLoading: false,
      optionsLoading: false,

      // Depot data
      allDepots: [],

      // Suburb autocomplete
      suburbSuggestions: [],

      // Results
      routeOptions: [],
      hasResults: false,
      noResults: false,

      // Detail panel
      selectedOption: null,
      depotCoordsCache: {},
      routeMapMarkers: [],
      routeMapLegs: [],
      mapLoading: false,
      initialBounds: null,
      tileUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',

      // Table headers
      tableHeaders: [
        { text: 'Latest Production', value: 'latest_production_date', sortable: true },
        { text: 'Latest Consolidation', value: 'latest_consolidation_date', sortable: true },
        { text: 'Delivery Ready', value: 'delivery_ready_date', sortable: true },
        { text: 'Price', value: 'price', sortable: true },
        { text: 'Total Legs', value: 'total_legs', sortable: true },
        { text: '', value: 'actions', sortable: false, width: '48px' },
      ],
    };
  },
  computed: {
    wmsDepots() {
      return this.allDepots
        .filter(d => d.wms)
        .sort((a, b) => (a.name > b.name) ? 1 : -1);
    },
    canShowOptions() {
      if (this.trackingNumber && this.trackingNumber.trim()) return true;
      return this.selectedDepots.length > 0 && this.deliveryDate && this.selectedSuburbData;
    },
    selectedOptionDepots() {
      if (!this.selectedOption || !this.selectedOption.depots || !Array.isArray(this.selectedOption.depots)) return [];
      return this.selectedOption.depots;
    },
    australiaBounds() {
      return L.latLngBounds([-10, 110], [-45, 155]);
    },
    latestProductionDate() {
      if (!this.selectedOption || !this.selectedOption.depots || !this.selectedOption.depots.length) return null;
      const firstHop = this.selectedOption.depots[0];
      if (!firstHop.departure_date) return null;
      const d = new Date(firstHop.departure_date + 'T00:00:00');
      d.setDate(d.getDate() - 1);
      return d.toISOString().slice(0, 10);
    },
    resultOriginDepotName() {
      if (!this.routeOptions.length) return '';
      const depots = this.routeOptions[0].depots;
      if (!depots || !depots.length) return '';
      return this.getDepotName(depots[0].depot_id) || ('Depot ' + depots[0].depot_id);
    },
    resultDestinationDepotName() {
      if (!this.routeOptions.length) return '';
      const depots = this.routeOptions[0].depots;
      if (!depots || !depots.length) return '';
      const last = depots[depots.length - 1];
      return this.getDepotName(last.depot_id) || ('Depot ' + last.depot_id);
    },
  },
  mounted() {
    this.fetchDepots();
  },
  watch: {
    suburbSearch(val) {
      if (!val || val.length < 2) return;
      if (this.suburbLoading) return;
      if (this.deliverySuburb && this.deliverySuburb === val) return;
      this.searchSuburbs(val);
    },
    selectedOption() {
      this.$nextTick(() => this.updateRouteMap());
    },
  },
  methods: {
    getLatestProductionDate(item) {
      if (!item || !item.depots || !item.depots.length) return null;
      const firstHop = item.depots[0];
      if (!firstHop.departure_date) return null;
      const d = new Date(firstHop.departure_date + 'T00:00:00');
      d.setDate(d.getDate() - 1);
      return d.toISOString().slice(0, 10);
    },
    hopColor(index) {
      if (index === 0) return 'green';
      if (index === this.selectedOptionDepots.length - 1) return 'red darken-1';
      return 'orange darken-1';
    },
    hopLabel(index) {
      if (index === 0) return 'Origin';
      if (index === this.selectedOptionDepots.length - 1) return 'Destination';
      return 'Transit';
    },
    onDeliveryDateChange(date) {
      this.deliveryDate = date;
    },
    allowedDeliveryDates(val) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const maxDate = new Date(today);
      maxDate.setDate(maxDate.getDate() + 14);
      const d = new Date(val + 'T00:00:00');
      return d >= today && d <= maxDate;
    },
    onSuburbSelect(displayVal) {
      if (!displayVal) {
        this.selectedSuburbData = null;
        return;
      }
      this.selectedSuburbData = this.suburbSuggestions.find(s => s.display === displayVal) || null;
    },
    async searchSuburbs(query) {
      this.suburbLoading = true;
      try {
        const response = await axios.get(process.env.API_URL + '/zones/autocomplete?s=' + encodeURIComponent(query));
        if (response.data && Array.isArray(response.data)) {
          this.suburbSuggestions = response.data.map(item => {
            const parsed = {};
            if (item.suburb) {
              const parts = item.suburb.split(',').map(p => p.trim());
              parsed.suburb_name = parts[0] || null;
              parsed.postcode = parts[1] || null;
            }
            return {
              ...item,
              ...parsed,
              display: item.suburb || '',
            };
          });
        }
      } catch (error) {
        this.suburbSuggestions = [];
      } finally {
        this.suburbLoading = false;
      }
    },
    getDepotName(depotId) {
      const depot = this.allDepots.find(d => d.id === depotId);
      return depot ? depot.name : null;
    },
    rowClass(item) {
      return this.selectedOption === item ? 'selected-row' : '';
    },
    async fetchDepots() {
      this.depotsLoading = true;
      try {
        const response = await axios.get(process.env.API_URL + '/depots');
        if (response.status === 200) {
          this.allDepots = response.data;
        }
      } catch (error) {
        this.$store.commit('displaySnackbar', 'Failed to load depots');
      } finally {
        this.depotsLoading = false;
      }
    },
    async showOptions() {
      this.optionsLoading = true;
      this.hasResults = false;
      this.noResults = false;
      this.selectedOption = null;
      this.routeOptions = [];

      try {
        let params;
        if (this.trackingNumber && this.trackingNumber.trim()) {
          params = { tracking: this.trackingNumber.trim() };
        } else {
          params = {
            origin_depots: JSON.stringify(this.selectedDepots),
            delivery_date: this.deliveryDate,
            postcode: this.selectedSuburbData.postcode,
            suburb: this.selectedSuburbData.suburb_name,
          };
        }
        const response = await axios.get(process.env.API_URL + '/linehauls/routes', { params });

        if (response.status === 200 && response.data) {
          const data = response.data;
          const routes = Array.isArray(data) ? data : (data.routes || data.options || []);

          if (routes.length > 0) {
            this.routeOptions = routes;
            this.hasResults = true;
          } else {
            this.noResults = true;
          }
        } else {
          this.noResults = true;
        }
      } catch (error) {
        console.error('Linehaul calculator error:', error);
        const msg = error.response && error.response.data && error.response.data.error
          ? error.response.data.error
          : 'Failed to fetch linehaul routes';
        this.$store.commit('displaySnackbar', 'ERROR: ' + msg);
        this.noResults = true;
      } finally {
        this.optionsLoading = false;
      }
    },
    onRouteMapReady() {
      if (this.selectedOption) this.updateRouteMap();
    },
    tooltipDirection(index) {
      const directions = ['top', 'bottom', 'right', 'left'];
      return directions[index % directions.length];
    },
    tooltipOffset(index) {
      const offsets = [[0, -14], [0, 14], [14, 0], [-14, 0]];
      return offsets[index % offsets.length];
    },
    createDepotIcon(index, total) {
      const color = index === 0 ? '#4CAF50' : (index === total - 1 ? '#E53935' : '#FB8C00');
      const size = 16;
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
        <circle cx="${size/2}" cy="${size/2}" r="${size/2 - 1}" fill="${color}" stroke="#fff" stroke-width="2"/>
      </svg>`;
      return L.divIcon({
        className: '',
        html: svg,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });
    },
    createLegIcon(legNumber, color) {
      const size = 28;
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
        <circle cx="${size/2}" cy="${size/2}" r="${size/2}" fill="${color}"/>
        <text x="${size/2}" y="${size/2 + 1}" text-anchor="middle" dominant-baseline="central" fill="#fff" font-size="12" font-weight="bold" font-family="Arial">${legNumber}</text>
      </svg>`;
      return L.divIcon({
        className: '',
        html: svg,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });
    },
    createArrowIcon(bearing, color) {
      const size = 22;
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
        <polygon points="11,2 19,19 11,14 3,19" fill="${color}" transform="rotate(${bearing}, 11, 11)"/>
      </svg>`;
      return L.divIcon({
        className: '',
        html: svg,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });
    },
    getBearing(from, to) {
      const dLng = (to[1] - from[1]) * Math.PI / 180;
      const lat1 = from[0] * Math.PI / 180;
      const lat2 = to[0] * Math.PI / 180;
      const y = Math.sin(dLng) * Math.cos(lat2);
      const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
      return ((Math.atan2(y, x) * 180 / Math.PI) + 360) % 360;
    },
    getDepotCoords(depotId) {
      if (this.depotCoordsCache[depotId]) return this.depotCoordsCache[depotId];
      const depot = this.allDepots.find(d => d.id == depotId);
      if (!depot) return null;
      const lat = parseFloat(depot.latitude);
      const lng = parseFloat(depot.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      const coords = { lat, lng };
      this.depotCoordsCache[depotId] = coords;
      return coords;
    },
    async fetchRoadRoute(from, to) {
      try {
        const url = `https://router.project-osrm.org/route/v1/driving/${from[1]},${from[0]};${to[1]},${to[0]}?overview=full&geometries=geojson`;
        const response = await fetch(url);
        const data = await response.json();
        if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
          return data.routes[0].geometry.coordinates.map(c => [c[1], c[0]]);
        }
      } catch (e) {
        console.warn('OSRM routing failed, falling back to straight line', e);
      }
      return [from, to];
    },
    pointAlongPath(path, fraction) {
      if (path.length < 2) return path[0];
      const lengths = [];
      let total = 0;
      for (let i = 1; i < path.length; i++) {
        const dx = path[i][0] - path[i - 1][0];
        const dy = path[i][1] - path[i - 1][1];
        const seg = Math.sqrt(dx * dx + dy * dy);
        lengths.push(seg);
        total += seg;
      }
      let target = total * fraction;
      for (let i = 0; i < lengths.length; i++) {
        if (target <= lengths[i]) {
          const t = lengths[i] > 0 ? target / lengths[i] : 0;
          return [
            path[i][0] + (path[i + 1][0] - path[i][0]) * t,
            path[i][1] + (path[i + 1][1] - path[i][1]) * t,
          ];
        }
        target -= lengths[i];
      }
      return path[path.length - 1];
    },
    async updateRouteMap() {
      this.routeMapMarkers = [];
      this.routeMapLegs = [];
      this.mapLoading = false;
      this.initialBounds = null;

      if (!this.selectedOption || !this.selectedOptionDepots.length) return;

      this.mapLoading = true;

      const depots = this.selectedOptionDepots;
      const coords = [];
      for (let i = 0; i < depots.length; i++) {
        const d = depots[i];
        const c = this.getDepotCoords(d.depot_id);
        if (c) {
          const name = this.getDepotName(d.depot_id) || ('Depot ' + d.depot_id);
          const isLast = i === depots.length - 1;
          const dateLabel = isLast
            ? (d.arrival_date ? '(arr: ' + d.arrival_date + ')' : '')
            : (d.departure_date ? '(dep: ' + d.departure_date + ')' : '');
          coords.push({ latLng: [c.lat, c.lng], name, dateLabel });
        }
      }

      this.routeMapMarkers = coords;

      const legColors = ['#1565C0', '#E53935', '#2E7D32', '#F57C00', '#6A1B9A', '#00838F'];
      const legs = [];
      for (let i = 0; i < coords.length - 1; i++) {
        const from = coords[i].latLng;
        const to = coords[i + 1].latLng;
        const path = await this.fetchRoadRoute(from, to);
        const midpoint = this.pointAlongPath(path, 0.5);
        const arrowPoint = this.pointAlongPath(path, 0.75);
        legs.push({
          path,
          color: legColors[i % legColors.length],
          midpoint,
          arrowPoint,
          bearing: this.getBearing(from, to),
        });
      }
      this.routeMapLegs = legs;

      await this.$nextTick();
      const map = this.$refs.routeMap ? this.$refs.routeMap.mapObject : null;
      if (map && coords.length >= 2) {
        const bounds = L.latLngBounds(coords.map(c => c.latLng));
        this.initialBounds = bounds;
        map.fitBounds(bounds, { padding: [40, 40] });
      }
      this.mapLoading = false;
    },
    resetMap() {
      const map = this.$refs.routeMap ? this.$refs.routeMap.mapObject : null;
      if (map && this.initialBounds) {
        map.fitBounds(this.initialBounds, { padding: [40, 40] });
      }
    },
    toggleDetails(item) {
      if (this.selectedOption === item) {
        this.selectedOption = null;
      } else {
        this.selectedOption = item;
      }
    },
  }
};
</script>

<style scoped>
.linehaul-calculator {
  background: #f0f2f5;
  min-height: 100vh;
}

/* ── Header ── */
.page-header {
  background: linear-gradient(135deg, #0d47a1 0%, #1565c0 40%, #1976d2 100%);
  box-shadow: 0 4px 20px rgba(13, 71, 161, 0.3);
}

.header-icon-badge {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── Form Card ── */
.form-card {
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.form-card-accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #1565c0, #42a5f5);
}

.search-btn {
  border-radius: 8px !important;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 8px rgba(21, 101, 192, 0.3) !important;
  transition: all 0.2s ease;
}

.search-btn:hover:not(:disabled) {
  box-shadow: 0 4px 16px rgba(21, 101, 192, 0.4) !important;
  transform: translateY(-1px);
}

/* ── Summary Banner ── */
.summary-banner {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-left: 4px solid #1565c0;
}

.summary-badge {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #43a047, #66bb6a);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* ── Table ── */
.table-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
  border: 1px solid rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

::v-deep .routes-table .v-data-table-header {
  background: #fafbfc;
}

::v-deep .routes-table .v-data-table-header th {
  font-size: 12px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #546E7A !important;
  border-bottom: 2px solid #e0e0e0 !important;
  padding-top: 14px !important;
  padding-bottom: 14px !important;
}

::v-deep .routes-table tbody td {
  font-size: 14px !important;
  padding-top: 14px !important;
  padding-bottom: 14px !important;
}

::v-deep .routes-table tbody tr {
  cursor: pointer;
  transition: all 0.15s ease;
}

::v-deep .routes-table tbody tr:hover {
  background-color: #e3f2fd !important;
}

::v-deep .routes-table tbody tr.selected-row {
  background-color: #bbdefb !important;
  box-shadow: inset 3px 0 0 #1565c0;
}

::v-deep .routes-table tbody tr.selected-row:hover {
  background-color: #90caf9 !important;
}

.expand-btn {
  transition: transform 0.2s ease;
}

/* ── Detail Panel ── */
.detail-card {
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1) !important;
  border: 1px solid rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.detail-card-header {
  background: linear-gradient(135deg, #0d47a1 0%, #1565c0 50%, #1976d2 100%);
}

.detail-header-badge {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── Stat Cards ── */
.stat-card {
  display: flex;
  align-items: center;
  padding: 18px 20px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.stat-card__icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  flex-shrink: 0;
}

.stat-card--blue .stat-card__icon { background: linear-gradient(135deg, #1565c0, #42a5f5); }
.stat-card--teal .stat-card__icon { background: linear-gradient(135deg, #00796b, #26a69a); }
.stat-card--indigo .stat-card__icon { background: linear-gradient(135deg, #283593, #5c6bc0); }
.stat-card--green .stat-card__icon { background: linear-gradient(135deg, #2e7d32, #66bb6a); }

.stat-card__label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #546e7a;
  margin-bottom: 2px;
}

.stat-card__value {
  font-size: 18px;
  font-weight: 700;
  color: #263238;
}

/* ── Section Headers ── */
.section-header {
  display: flex;
  align-items: center;
}

/* ── Map ── */
.map-container {
  border: 2px solid #e0e0e0 !important;
}

.reset-map-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3) !important;
}

.map-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 8px;
}

/* ── Hop Cards ── */
.hop-card {
  border: 1px solid #e0e0e0 !important;
  transition: all 0.15s ease;
}

.hop-card:hover {
  border-color: #bbdefb !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.hop-date-badge {
  display: inline-flex;
  align-items: center;
  background: #f5f5f5;
  border-radius: 6px;
  padding: 4px 10px;
}

/* ── Timeline overrides ── */
::v-deep .v-timeline-item__dot--large {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
</style>



// WEBPACK FOOTER //
// src/components/pages/Delivery/LinehaulCalculator.vue